import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Shared/contexts/AuthContext';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import { Button } from '../../Shared/ui/button';
import { Input } from '../../Shared/ui/input';
import { Card } from '../../Shared/ui/card';

const Login: React.FC = () => {
  const [loginMode, setLoginMode] = useState<'parent' | 'student'>('parent');

  // Parent login fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Student login fields
  const [parentEmail, setParentEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [pin, setPin] = useState('');

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleParentSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await apiClient.login({ email, password });
      login(response.user);
      navigate(response.user.email_verified_at ? '/home' : '/verify-email');
    } catch (err: unknown) {
      setError(extractApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');

    if (pin.length !== 5) {
      setError('PIN must be exactly 5 digits.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.studentLogin({
        parent_email: parentEmail,
        first_name: firstName,
        pin,
      });
      login(response.user);
      navigate('/home');
    } catch (err: unknown) {
      setError(extractApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <h1 className="text-2xl font-bold text-blue-600">Numbie Learning</h1>
          </button>
          <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign Up
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center p-4 pt-16">
        <Card className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
            <p className="text-gray-600">Sign in to continue learning.</p>
          </div>

          {/* Login Mode Tabs */}
          <div className="flex mb-6 border-b border-gray-200">
            <button
              type="button"
              onClick={() => {
                setLoginMode('parent');
                setError('');
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                loginMode === 'parent'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Parent Login
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginMode('student');
                setError('');
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                loginMode === 'student'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Student Login
            </button>
          </div>

          {/* Parent Login Form */}
          {loginMode === 'parent' && (
            <form onSubmit={(e) => void handleParentSubmit(e)} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}

          {/* Student Login Form */}
          {loginMode === 'student' && (
            <form onSubmit={(e) => void handleStudentSubmit(e)} className="space-y-5">
              <div>
                <label
                  htmlFor="parent-email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Parent&apos;s Email
                </label>
                <Input
                  id="parent-email"
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="parent@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="first-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Your First Name
                </label>
                <Input
                  id="first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoComplete="given-name"
                  placeholder="Enter your first name"
                />
              </div>

              <div>
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                  Your PIN
                </label>
                <Input
                  id="pin"
                  type="text"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  required
                  maxLength={5}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  placeholder="12345"
                  className={pin.length > 0 && pin.length !== 5 ? 'border-red-300' : ''}
                />
                {pin.length > 0 && pin.length !== 5 && (
                  <p className="mt-1 text-xs text-red-600">PIN must be exactly 5 digits</p>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading || pin.length !== 5}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;
